import { httpApi } from '@/api/http.api'
import type { ServiceOrderKindDto } from '@/api/service-order.api'

export type ServiceRequestStatusDto = 'NEW' | 'CONVERTED' | 'REJECTED'

export type ServiceRequestDto = {
  id: string
  requestedAt: string
  status: ServiceRequestStatusDto
  clientName: string
  clientAddress: string | null
  clientOrderingParty: string | null
  clientContact: string | null
  firmId: string | null
  description: string
  deviceId: string | null
  deviceName: string | null
  deviceType: string | null
  neededAction: string | null
  /** Rodzaj wybrany przez klienta; `null` w zgłoszeniach sprzed formularza klienta. */
  kind: ServiceOrderKindDto | null
  /** Pierwsze otwarcie przez dowolnego kierownika serwisu; `null` oznacza zgłoszenie nowe. */
  seenAt: string | null
  /** Zlecenie, w które zamieniono zgłoszenie. */
  orderId: string | null
  rejectedAt: string | null
  createdAt: string
  updatedAt: string
}

export type ServiceRequestsQuery = {
  search?: string
  seen?: 'new' | 'seen'
  /** Zakres daty zgłoszenia, RRRR-MM-DD. */
  requestedFrom?: string
  requestedTo?: string
}

const SERVICE_REQUESTS = 'service-requests'

/** Zgłoszenia czekające na decyzję kierownika; zamienione i odrzucone nie wracają na listę. */
export const getServiceRequests = (query: ServiceRequestsQuery = {}): Promise<ServiceRequestDto[]> =>
  httpApi.get<ServiceRequestDto[]>(SERVICE_REQUESTS, { params: query }).then(({ data }) => data)

export const getServiceRequest = (id: string): Promise<ServiceRequestDto> =>
  httpApi.get<ServiceRequestDto>(`${SERVICE_REQUESTS}/${id}`).then(({ data }) => data)

export const markServiceRequestSeen = (id: string): Promise<void> =>
  httpApi.patch<void>(`${SERVICE_REQUESTS}/${id}/seen`).then(({ data }) => data)

/** Zgłoszenie zamienione w zlecenie albo już odrzucone kończy się błędem 409. */
export const rejectServiceRequest = (id: string): Promise<void> =>
  httpApi.patch<void>(`${SERVICE_REQUESTS}/${id}/reject`).then(({ data }) => data)

/** Stan zgłoszenia widziany przez klienta — liczy go backend z decyzji kierownika i przebiegu zlecenia. */
export type CustomerServiceRequestStateDto = 'WAITING' | 'REJECTED' | 'PLANNED' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED'

export type CustomerServiceRequestDto = {
  id: string
  requestedAt: string
  kind: ServiceOrderKindDto | null
  description: string
  state: CustomerServiceRequestStateDto
  orderNumber: string | null
  expectedAt: string | null
  closedAt: string | null
}

export type CreateDeviceServiceRequestPayload = {
  kind: ServiceOrderKindDto
  description: string
  /** Osoba do kontaktu, gdy nie jest nią zgłaszający. */
  contactName?: string
  contactPhone?: string
}

const deviceServiceRequests = (deviceId: string) => `devices/${deviceId}/service-requests`

/** Zgłoszenia, które zalogowany klient wysłał dla urządzenia, najnowsze pierwsze. */
export const getDeviceServiceRequests = (deviceId: string): Promise<CustomerServiceRequestDto[]> =>
  httpApi.get<CustomerServiceRequestDto[]>(deviceServiceRequests(deviceId)).then(({ data }) => data)

export const createDeviceServiceRequest = (
  deviceId: string,
  payload: CreateDeviceServiceRequestPayload,
): Promise<CustomerServiceRequestDto> =>
  httpApi.post<CustomerServiceRequestDto>(deviceServiceRequests(deviceId), payload).then(({ data }) => data)
